/**
 * PostHog 初期化 (Phase 3).
 *
 * 行動ログ + フィードバック収集。
 * API Key は環境変数 VITE_POSTHOG_KEY で注入。
 * 未設定時は noop（開発時にログを汚さない）。
 *
 * Opt-out 設計 (PR-POSTHOG L2):
 *   - init 時に opt_out_capturing_by_default: true を渡し、初期状態は capture しない
 *   - localStorage の petacms_analytics_consent='granted' なら opt-in
 *   - 同意UIから setAnalyticsConsent(true|false) を呼ぶことで opt-in/opt-out 切替可能
 *   - 同意なしのまま trackPageView/trackEvent/identifyUser を呼んでも PostHog SDK 側で capture されない
 */

import posthog from 'posthog-js';

function readEnv(name: 'VITE_POSTHOG_KEY' | 'VITE_POSTHOG_HOST'): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.[name];
}

function postHogKey(): string {
  return readEnv('VITE_POSTHOG_KEY') ?? '';
}

function postHogHost(): string {
  return readEnv('VITE_POSTHOG_HOST') ?? 'https://us.i.posthog.com';
}

const CONSENT_STORAGE_KEY = 'petacms_analytics_consent';

let initialized = false;

function readConsent(): boolean {
  try {
    return globalThis.localStorage?.getItem(CONSENT_STORAGE_KEY) === 'granted';
  } catch {
    return false;
  }
}

export function initPostHog(): void {
  if (initialized) return;
  const key = postHogKey();
  if (!key) return;

  const consentGranted = readConsent();

  posthog.init(key, {
    api_host: postHogHost(),
    // SPA なので手動で pageview を送る
    capture_pageview: false,
    // セッション録画は Phase 7+ で検討
    disable_session_recording: true,
    // 同意UI実装まで初期状態は opt-out (capture されない)。
    // localStorage の petacms_analytics_consent='granted' があれば後段で opt-in。
    opt_out_capturing_by_default: true,
    persistence: 'localStorage',
    // PII は送らない
    sanitize_properties: (properties) => {
      // email 等が混入しないよう、$set/$set_once は除去
      delete properties.$set;
      delete properties.$set_once;
      return properties;
    },
  });

  if (consentGranted) {
    posthog.opt_in_capturing();
  }

  initialized = true;
}

/**
 * 同意UIから呼ぶ。granted=true で opt-in、false で opt-out + reset。
 */
export function setAnalyticsConsent(granted: boolean): void {
  try {
    globalThis.localStorage?.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');
  } catch {
    // localStorage 不可環境では memory only
  }
  if (!postHogKey() || !initialized) return;
  if (granted) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
    posthog.reset();
  }
}

/**
 * 同意状態を取得 (UI 表示用)。
 */
export function hasAnalyticsConsent(): boolean {
  return readConsent();
}

/**
 * ページビューを送信。TanStack Router の onRouteChange で呼ぶ。
 * 未設定 or opt-out 中は noop (PostHog SDK 側でも保証)。
 */
export function trackPageView(path: string): void {
  if (!postHogKey()) return;
  posthog.capture('$pageview', { $current_url: path });
}

/**
 * カスタムイベントを送信。
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!postHogKey()) return;
  posthog.capture(event, properties);
}

/**
 * ユーザー識別。ログイン成功時に呼ぶ。
 */
export function identifyUser(userId: string): void {
  if (!postHogKey()) return;
  posthog.identify(userId);
}

/**
 * ユーザーリセット。ログアウト時に呼ぶ。
 */
export function resetUser(): void {
  if (!postHogKey()) return;
  posthog.reset();
}
